import { Flex, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateService } from '@shared/services/date';
import { Text } from '@shared/ui/antd';

import type { AgentBackupItem } from './lib/types';
import styles from './backups.module.scss';
import { Settings } from './ui/settings/settings';

interface BackupsTableProps {
  backups: AgentBackupItem[];
  onRestoreBackup: (backupId: number) => void;
  onRenameBackup: (backupId: number) => void;
  onDeleteBackup: (backupId: number) => void;
}

export const BackupsTable: FC<BackupsTableProps> = ({
  backups,
  onRestoreBackup,
  onRenameBackup,
  onDeleteBackup,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.backups' });

  const columns = useMemo<ColumnsType<AgentBackupItem>>(
    () => {
      const renderHeader = (title: string) => (
        <Tooltip title={title}>
          <span className={styles.headerLabel}>{title}</span>
        </Tooltip>
      );

      const renderCell = (value: string, className?: string) => (
        <Tooltip title={value}>
          <span className={[styles.cellText, className].filter(Boolean).join(' ')}>{value}</span>
        </Tooltip>
      );

      return [
        {
          title: renderHeader(t('table.name')),
          dataIndex: 'title',
          key: 'title',
          width: 150,
          ellipsis: true,
          render: (value: string) => renderCell(value),
        },
        {
          title: renderHeader(t('table.author')),
          dataIndex: 'author',
          key: 'author',
          width: 140,
          ellipsis: true,
          render: (value: string) => renderCell(value),
        },
        {
          title: renderHeader(t('table.date')),
          dataIndex: 'createdAt',
          key: 'createdAt',
          width: 145,
          ellipsis: true,
          render: (value: string) =>
            renderCell(DateService.format(value, 'DD.MM.YYYY, HH:mm'), styles.cellTextMuted),
        },
        {
          title: '',
          key: 'actions',
          width: 64,
          align: 'center',
          render: (_: unknown, backup) => (
            <Flex align="center" justify="center">
              <Settings
                onRestore={() => onRestoreBackup(backup.id)}
                onRename={() => onRenameBackup(backup.id)}
                onDelete={() => onDeleteBackup(backup.id)}
              />
            </Flex>
          ),
        },
      ];
    },
    [onDeleteBackup, onRenameBackup, onRestoreBackup, t],
  );

  return (
    <Table
      columns={columns}
      dataSource={backups}
      rowKey="id"
      pagination={false}
      tableLayout="fixed"
      scroll={backups.length > 6 ? { y: 360 } : undefined}
      className={styles.table}
      locale={{
        emptyText: (
          <Text variant="sm" className={styles.emptyText}>
            {t('table.empty')}
          </Text>
        ),
      }}
    />
  );
};
